import React, { useEffect } from "react";
import { Stack, IStackStyles } from "@fluentui/react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import oidcConfig from "../config/oidcConfig";
import { AuthProvider } from "react-oidc-context";
import { AppSettingsProvider } from "../context/AppSettingsContext";
import { MaintenanceProvider, useMaintenance, registerMaintenanceSetter } from "../context/MaintenanceContext";
import Callback from "./Callback/Callback";
import Login from "./Login/Login";
import LayoutWithAuth from "./LayoutWithAuth/LayoutWithAuth";
import ViewRequests from "./ViewRequestsRoot/ViewRequests/ViewRequests";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import NewRequestForInformation from "./NewRequestForInformation/NewRequestForInformation";
import RequestForInformation from "./RequestForInformation/RequestForInformation";
import EUSLB from "./Admin/EUSLB/EUSLB";
import Periods from "./Admin/Periods/Periods";
import Reminder from "./Admin/Reminder/Reminder";
import FieldGuidance from "./Admin/FieldGuidance/FieldGuidance";
import POIList from "./ManageSystemData/POIList/POIList";
import AssessmentList from "./ManageSystemData/Assessments/AssessmentList";
import CAContactList from "./ManageSystemData/ContactList/CAContactList";
import Version from "./Version/Version";
import {
  ALL_ROLES, BULK_DELETE_ASSESSMENTS_ROLES,
  CONF_SYS_DETAILS_ROLES,
  MANAGE_SYS_DATA_CA_CONTACT_LIST_ROLES,
  MANAGE_SYS_DATA_POI_LIST_ROLES,
  NAV_KEY_CA_CONTACT_LIST,
  NAV_KEY_EU_SEC_LEG,
  NAV_KEY_FIELD_GUIDANCE,
  NAV_KEY_PERIODS,
  NAV_KEY_POI_LIST,
  NAV_KEY_POI_SEARCH_SCENARIOS,
  NAV_KEY_REMINDER,
  NAV_KEY_SEARCH_POI, NAV_KEY_VIEW_EMAIL_CONFIGURATION,
  NAV_KEY_VIEW_REQUESTS, NAV_KEY_VIEW_UPLOADED_RECORDS_LIST, NAV_KEY_VIEW_VALIDATION_RESULTS,
  NEW_REQ_FOR_INFO_ROLES,
  REQ_FOR_INFO_ROLES,
  SEARCH_CONTACTS_ROLES,
  SEARCH_POI_ROLES,
  VIEW_REQUESTS_ROLES,
  NAV_KEY_CA_END_USER_ASSIGNMENT,
  CA_END_USER_ASSIGNMENT_ROLES,
} from "../constants/EiopaFnPConstants";
import UnauthorizedPage from "./UnauthorizedPage/UnauthorizedPage";
import SearchForPoi from "./SearchForPOI/SearchForPOI";
import SearchContacts from "./SearchContacts/SearchContacts";
import POISearchScenarios from "./Admin/POISearchScenarios/POISearchScenarios";
import PageNotFound from "./PageNotFound/PageNotFound";
import ListOfFilesUploaded from "./ListOfFilesUploaded/ListOfFilesUploaded";
import ViewValidationResults from "./ViewValidationResults/ViewValidationResults";
import EmailConfiguration from "./ManageSystemData/EmailConfiguration/EmailConfiguration";
import BulkDeleteAssessments from "./BulkDeleteAssessments/BulkDeleteAssessments.tsx";
import Maintenance from "./Maintenance/Maintenance";
import CAEndUserAssignment from "./Admin/CAEndUserAssignment/CAEndUserAssignment";

const stackStyles: IStackStyles = {
  root: [
    {
      height: "auto",
      fontFamily: "'Segoe UI', Arial, sans-serif",
    },
  ],
};

// Inner component that uses the maintenance context
const AppContent: React.FunctionComponent = () => {
  const { isMaintenanceMode, setMaintenanceMode } = useMaintenance();
  const navigate = useNavigate();
  const location = useLocation();

  // Register the maintenance setter so it can be called from outside React
  useEffect(() => {
    registerMaintenanceSetter(setMaintenanceMode);
  }, [setMaintenanceMode]);

  // When maintenance mode turns on, clean up the URL to site root without changing content
  useEffect(() => {
    if (isMaintenanceMode) {
      if (location.pathname !== '/' || location.search) {
        navigate('/', { replace: true });
      }
    }
  }, [isMaintenanceMode, location.pathname, location.search, navigate]);

  // If maintenance mode is active, render the Maintenance page without changing URL
  if (isMaintenanceMode) {
    return <Maintenance />;
  }

  // Otherwise, render the normal routes
  return (
    <div className="app-container">
      <Stack styles={stackStyles}>
        {
          <Routes>
            <Route path="/" Component={Login} />
            <Route path="/Home" Component={Callback} />
            <Route
              path="/view-requests"
              element={
                <LayoutWithAuth
                  comp={<ViewRequests />}
                  allowedRoles={VIEW_REQUESTS_ROLES}
                  selectedNav={NAV_KEY_VIEW_REQUESTS}
                />
              }
            />
            <Route
              path="/search-poi"
              element={
                <LayoutWithAuth
                  allowedRoles={SEARCH_POI_ROLES}
                  comp={<SearchForPoi />}
                  selectedNav={NAV_KEY_SEARCH_POI}
                />
              }
            />
            <Route
              path="/search-contacts"
              element={
                <LayoutWithAuth
                  allowedRoles={SEARCH_CONTACTS_ROLES}
                  comp={<SearchContacts />}
                  selectedNav={"search-contacts"}
                />
              }
            />
            <Route
              path="/request-for-information/:reqNo"
              element={
                <LayoutWithAuth
                  allowedRoles={REQ_FOR_INFO_ROLES}
                  comp={<RequestForInformation />}
                />
              }
            />
            <Route
              path="/new-request-for-information/:poiId?"
              element={
                <LayoutWithAuth
                  allowedRoles={NEW_REQ_FOR_INFO_ROLES}
                  comp={<NewRequestForInformation />}
                />
              }
            />
            <Route
              path="/admin/eu-sec-leg-bases"
              element={
                <LayoutWithAuth
                  comp={<EUSLB />}
                  allowedRoles={CONF_SYS_DETAILS_ROLES}
                  selectedNav={NAV_KEY_EU_SEC_LEG}
                />
              }
            />
            <Route
              path="/admin/reminder"
              element={
                <LayoutWithAuth
                  comp={<Reminder />}
                  allowedRoles={CONF_SYS_DETAILS_ROLES}
                  selectedNav={NAV_KEY_REMINDER}
                />
              }
            />
            <Route
              path="/admin/periods"
              element={
                <LayoutWithAuth
                  comp={<Periods />}
                  allowedRoles={CONF_SYS_DETAILS_ROLES}
                  selectedNav={NAV_KEY_PERIODS}
                />
              }
            />
            <Route
              path="/admin/poi-search-scenarios"
              element={
                <LayoutWithAuth
                  comp={<POISearchScenarios />}
                  allowedRoles={CONF_SYS_DETAILS_ROLES}
                  selectedNav={NAV_KEY_POI_SEARCH_SCENARIOS}
                />
              }
            />
            <Route
              path="/admin/field-guidance"
              element={
                <LayoutWithAuth
                  comp={<FieldGuidance />}
                  allowedRoles={CONF_SYS_DETAILS_ROLES}
                  selectedNav={NAV_KEY_FIELD_GUIDANCE}
                />
              }
            />
            <Route
              path="/manage-system-data/poi-list"
              element={
                <LayoutWithAuth
                  comp={<POIList />}
                  allowedRoles={MANAGE_SYS_DATA_POI_LIST_ROLES}
                  selectedNav={NAV_KEY_POI_LIST}
                />
              }
            />
            <Route
              path="/manage-system-data/poi-list/assessments/:reqNo"
              element={
                <LayoutWithAuth
                  comp={<AssessmentList />}
                  allowedRoles={MANAGE_SYS_DATA_POI_LIST_ROLES}
                />
              }
            />
            <Route
              path="/manage-system-data/ca-contact-list"
              element={
                <LayoutWithAuth
                  comp={<CAContactList />}
                  allowedRoles={MANAGE_SYS_DATA_CA_CONTACT_LIST_ROLES}
                  selectedNav={NAV_KEY_CA_CONTACT_LIST}
                />
              }
            />
            <Route
                path="/manage-system-data/view-uploaded-records"
                element={
                  <LayoutWithAuth
                      comp={<ListOfFilesUploaded />}
                      allowedRoles={MANAGE_SYS_DATA_POI_LIST_ROLES}
                      selectedNav={NAV_KEY_VIEW_UPLOADED_RECORDS_LIST}
                  />
                }
            />
            <Route
                path="/manage-system-data/email-configuration"
                element={
                  <LayoutWithAuth
                      comp={<EmailConfiguration />}
                      allowedRoles={MANAGE_SYS_DATA_POI_LIST_ROLES}
                      selectedNav={NAV_KEY_VIEW_EMAIL_CONFIGURATION}
                  />
                }
            />
            <Route
                path="/manage-system-data/eu-legal-basis-assignments"
                element={
                  <LayoutWithAuth
                      comp={<CAEndUserAssignment />}
                      allowedRoles={CA_END_USER_ASSIGNMENT_ROLES}
                      selectedNav={NAV_KEY_CA_END_USER_ASSIGNMENT}
                  />
                }
            />
            <Route
                path="/manage-system-data/view-validation-results/:fileName?/:encryptedId?"
                element={
                  <LayoutWithAuth
                      comp={<ViewValidationResults />}
                      allowedRoles={MANAGE_SYS_DATA_POI_LIST_ROLES}
                      selectedNav={NAV_KEY_VIEW_VALIDATION_RESULTS}
                  />
                }
            />
            <Route
              path="/bulk-delete-assessments"
                element={
                    <LayoutWithAuth
                    comp={<BulkDeleteAssessments/>}
                    allowedRoles={BULK_DELETE_ASSESSMENTS_ROLES}
                    />
                }
            />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/version" element={<Version />} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route path="*" element={
              <LayoutWithAuth
                  comp={<PageNotFound />}
                  allowedRoles={ALL_ROLES}
              />
            } />
          </Routes>
        }
      </Stack>
    </div>
  );
};

export const App: React.FunctionComponent = () => {
  initializeIcons(undefined, { disableWarnings: true });

  return (
    <BrowserRouter>
      <AuthProvider {...oidcConfig}>
        <AppSettingsProvider>
          <MaintenanceProvider>
            <AppContent />
          </MaintenanceProvider>
        </AppSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};
